import { Button } from '@/components/ui/button'
import { DrawerClose, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui/drawer'
import { InputField } from '@/components/ui/input-field'
import {
	usePurchasePhoneNumber,
	useReleasePhoneNumber,
	useUpdatePhoneNumber,
} from '@/queries/phone-numbers'
import { useStytchOrganization } from '@stytch/react/b2b'
import type { PhoneNumber } from 'honeygrid-types'
import { Phone, X } from 'lucide-react'
import { useState } from 'react'

interface PhoneNumberRecordPanelProps {
	onClose: () => void
	phoneNumber?: PhoneNumber
	isNew?: boolean
}

export function PhoneNumberRecordPanel({
	onClose,
	phoneNumber,
	isNew = false,
}: PhoneNumberRecordPanelProps) {
	const { organization } = useStytchOrganization()
	const [phoneNumberValue, setPhoneNumberValue] = useState(phoneNumber?.phone_number || '')
	const [areaCode, setAreaCode] = useState('')
	const [friendlyName, setFriendlyName] = useState(phoneNumber?.friendly_name || '')

	const purchasePhoneNumber = usePurchasePhoneNumber()
	const updatePhoneNumber = useUpdatePhoneNumber()
	const releasePhoneNumber = useReleasePhoneNumber()

	const isSaving =
		purchasePhoneNumber.isPending ||
		updatePhoneNumber.isPending ||
		releasePhoneNumber.isPending

	const handleSave = async () => {
		if (!organization?.organization_id) return

		if (isNew) {
			// Purchase new phone number
			await purchasePhoneNumber.mutateAsync({
				workspaceId: organization.organization_id,
				phoneNumber: phoneNumberValue || undefined,
				areaCode: areaCode || undefined,
				friendlyName: friendlyName || undefined,
			})
		} else if (phoneNumber) {
			// Update existing phone number
			await updatePhoneNumber.mutateAsync({
				workspaceId: organization.organization_id,
				phoneNumberId: phoneNumber.uuid,
				updates: {
					friendly_name: friendlyName,
				},
			})
		}
		onClose()
	}

	const handleDelete = async () => {
		if (!organization?.organization_id || !phoneNumber) return

		if (confirm('Are you sure you want to release this phone number back to the pool?')) {
			await releasePhoneNumber.mutateAsync({
				workspaceId: organization.organization_id,
				phoneNumberId: phoneNumber.uuid,
			})
			onClose()
		}
	}

	const handleCancel = () => {
		setPhoneNumberValue(phoneNumber?.phone_number || '')
		setAreaCode('')
		setFriendlyName(phoneNumber?.friendly_name || '')
		onClose()
	}

	const canSave = isNew ? phoneNumberValue.trim() || areaCode.trim() : true

	return (
		<div className="flex flex-col h-full">
			<DrawerHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
				<DrawerTitle>
					<div className="flex items-center gap-2">
						<Phone className="h-5 w-5" />
						{isNew ? 'Purchase Phone Number' : 'Edit Phone Number'}
					</div>
				</DrawerTitle>
				<DrawerClose asChild>
					<Button
						variant="ghost"
						size="icon"
						onClick={onClose}
						className="hover:bg-primary hover:text-primary-foreground"
					>
						<X className="h-4 w-4" />
					</Button>
				</DrawerClose>
			</DrawerHeader>

			<div className="flex-1 px-4 overflow-auto">
				<div className="space-y-6">
					<InputField
						label="Friendly Name"
						value={friendlyName}
						onChange={(e) => setFriendlyName(e.target.value)}
						placeholder="Customer Support Line"
						disabled={isSaving}
					/>

					{isNew ? (
						<>
							<InputField
								label="Specific Phone Number (optional)"
								value={phoneNumberValue}
								onChange={(e) => setPhoneNumberValue(e.target.value)}
								placeholder="+14155551234"
								type="tel"
								disabled={isSaving}
								subtext="Provide a specific number to purchase, or leave blank to use area code"
							/>

							<InputField
								label="Area Code (optional)"
								value={areaCode}
								onChange={(e) => setAreaCode(e.target.value)}
								placeholder="415"
								maxLength={3}
								disabled={isSaving || !!phoneNumberValue}
								subtext="3-digit area code to search for available numbers"
							/>
						</>
					) : (
						<>
							<InputField
								label="Phone Number"
								value={phoneNumber?.phone_number || ''}
								disabled
								readOnly
							/>

							<InputField label="Status" value={phoneNumber?.status || ''} disabled readOnly />

							<div className="flex items-center gap-2">
								<InputField
									label="Voice Capable"
									value={phoneNumber?.capabilities?.voice ? 'Yes' : 'No'}
									disabled
									readOnly
									className="flex-1"
								/>
								<InputField
									label="SMS Capable"
									value={phoneNumber?.capabilities?.sms ? 'Yes' : 'No'}
									disabled
									readOnly
									className="flex-1"
								/>
							</div>
						</>
					)}
				</div>
			</div>

			<DrawerFooter>
				<div className="flex gap-2 w-full">
					<Button onClick={handleSave} disabled={isSaving || !canSave} className="flex-1">
						{isSaving ? 'Saving...' : isNew ? 'Purchase' : 'Save'}
					</Button>
					<Button variant="outline" onClick={handleCancel} disabled={isSaving}>
						Cancel
					</Button>
					{!isNew && (
						<Button
							variant="destructive"
							onClick={handleDelete}
							disabled={isSaving}
						>
							Release
						</Button>
					)}
				</div>
			</DrawerFooter>
		</div>
	)
}
